import { supabase } from '@/lib/supabase';
import { assertRowsUpdated } from '@/lib/rlsUpdate';
import { centralMonth } from '@/lib/businessTime';
import { getEmployees } from '@/lib/employee-db';
import {
  ODB_TEXT_COLUMN,
  ON_DEMAND_BILLING_COLUMNS,
} from './config/onDemandBilling';
import { TAX_FILE_READY_COLUMNS } from './config/taxFileReady';

// Ported from the standalone workflow-v2 app (frontend/workflow-v2
// src/lib/api/workflow.ts) as part of the in-portal consolidation. Same data
// rules: ONE load call per view, writes through V1's own merge RPC with
// V1-identical payloads.

export interface WorkflowMonth {
  id: string;
  name: string;
  /** First-of-month ISO date from the spreadsheets.month column */
  month: string;
}

export interface WorkflowColumn {
  id: string;
  name: string;
  column_type: string;
  column_order: number;
}

export interface WorkflowRow {
  id: string;
  clientId: string | null;
  order: number;
  /** JSONB row_data — keys are the column display names */
  data: Record<string, unknown>;
  /** From clients table (only populated by the V2 load function) */
  clientStatus: string | null;
  entityType: string | null;
  hasCredentials: boolean;
}

export interface EmployeeOption {
  id: string;
  name: string;
  /** Saved profile avatar image (profiles.avatar_url) */
  avatarUrl: string | null;
  /** Saved profile avatar color (profiles.avatar_color, hex) */
  avatarColor: string | null;
}

const WORKFLOW_NAME_PATTERN = /TIFPA\s+Workflow/i;

// Arg-taking RPC calls collapse under the generated generics — loosely-typed
// alias per the established pattern; results validated by the mapping code.
const rpc = supabase.rpc.bind(supabase) as (
  fn: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;

/**
 * All monthly TIFPA Workflow spreadsheets, newest first. Matched by name (not
 * builtin_key: month duplication generates a unique builtin_key per copy).
 */
export async function listWorkflowMonths(): Promise<WorkflowMonth[]> {
  const { data, error } = await rpc('get_spreadsheets_with_creator');
  if (error)
    throw new Error(`Failed to load workflow months: ${error.message}`);

  const rows = (data ?? []) as Array<Record<string, unknown>>;
  return rows
    .filter(
      row =>
        typeof row.spreadsheet_name === 'string' &&
        WORKFLOW_NAME_PATTERN.test(row.spreadsheet_name) &&
        row.spreadsheet_month &&
        !row.spreadsheet_is_archived &&
        !row.spreadsheet_is_template
    )
    .map(row => ({
      id: String(row.spreadsheet_id),
      name: String(row.spreadsheet_name),
      month: String(row.spreadsheet_month),
    }))
    .sort((a, b) => b.month.localeCompare(a.month));
}

/** The month matching today (in CENTRAL time — the sheet a remote viewer
 *  lands on must match the office's month), or the newest as a fallback. */
export function resolveCurrentMonth(
  months: WorkflowMonth[]
): WorkflowMonth | null {
  if (months.length === 0) return null;
  const currentKey = centralMonth();
  return months.find(m => m.month.startsWith(currentKey)) ?? months[0];
}

/**
 * Load one workflow month's columns + rows in ONE call.
 *
 * Preferred: the V2-owned loader get_tifpa_workflow_data_for_v2
 * (sql/workflow-v2/001 — secure clients join + decrypted PII +
 * client_status/entity_type/has_credentials). Falls back to V1's secure RPC
 * (get_tifpa_workflow_data_secure — essential for identity fields, which
 * live ENCRYPTED in the clients table, not row_data), then the generic RPC.
 */
export async function loadWorkflowData(spreadsheetId: string): Promise<{
  columns: WorkflowColumn[];
  rows: WorkflowRow[];
}> {
  let { data, error } = await rpc('get_tifpa_workflow_data_for_v2', {
    spreadsheet_uuid: spreadsheetId,
  });
  if (error) {
    // Degraded mode: no clients join, so client_status badges, entity_type,
    // and has_credentials stay empty. Fix: run sql/workflow-v2/001.
    console.warn(
      `[WorkflowV2] get_tifpa_workflow_data_for_v2 unavailable (${error.message}); ` +
        'falling back to V1 loader — status badges/entity/credentials will be missing. ' +
        'Run sql/workflow-v2/001_get_tifpa_workflow_data_for_v2.sql in the SQL editor.'
    );
    ({ data, error } = await rpc('get_tifpa_workflow_data_secure', {
      spreadsheet_uuid: spreadsheetId,
    }));
  }
  if (error) {
    ({ data, error } = await rpc('get_spreadsheet_data', {
      spreadsheet_uuid: spreadsheetId,
    }));
  }
  if (error) throw new Error(`Failed to load workflow data: ${error.message}`);

  // The RPC returns an array with one { columns, rows } object
  const payload = ((Array.isArray(data) ? data[0] : data) ?? {}) as {
    columns?: Array<Record<string, unknown>>;
    rows?: Array<Record<string, unknown>>;
  };

  const columns: WorkflowColumn[] = (payload.columns ?? []).map(c => ({
    id: String(c.id),
    name: String(c.name ?? c.column_name ?? ''),
    column_type: String(c.column_type ?? c.type ?? 'text'),
    column_order: Number(c.column_order ?? c.order ?? 0),
  }));

  // "ACH Payor" / "1099" / "S Corp Election" and the Tax File Ready flags
  // are clients-table fields (ach_payor / service_1099 / s_corp_election /
  // tax_* — sql/workflow-v2/011) with NO spreadsheet_columns row — unlike
  // Bookkeeping/Sales Tax/Payroll, which are real V1 columns whose values
  // the loader overlays. The table and detail sheet only render tab columns
  // present in this live list, so inject virtual columns; values come from
  // the loader overlay (sql/workflow-v2/009 + 011) and writes go through
  // updateAchPayorFlag / updateClientServiceField.
  const virtualColumns: Array<{ name: string; type: string }> = [
    { name: 'ACH Payor', type: 'boolean' },
    { name: '1099', type: 'boolean' },
    { name: 'S Corp Election', type: 'boolean' },
    // Tax File Ready tab (all client-canonical, no month carry-over)
    ...TAX_FILE_READY_COLUMNS.map(name => ({
      name,
      type: name === '1099K' ? 'doc_1099k' : 'boolean',
    })),
    // On Demand Billing tab (also client-canonical)
    ...ON_DEMAND_BILLING_COLUMNS.map(name => ({
      name,
      type: name === ODB_TEXT_COLUMN ? 'text' : 'date_with_user',
    })),
  ];
  for (const { name: flagName, type } of virtualColumns) {
    if (!columns.some(c => c.name === flagName)) {
      columns.push({
        id: `virtual-${flagName.toLowerCase().replace(/\s+/g, '-')}`,
        name: flagName,
        column_type: type,
        column_order: Number.MAX_SAFE_INTEGER,
      });
    }
  }

  const rows: WorkflowRow[] = (payload.rows ?? []).map(r => ({
    id: String(r.row_id ?? r.id),
    clientId: (r.client_id as string | null) ?? null,
    order: Number(r.row_order ?? r.order ?? 0),
    data: (r.row_data ?? r.data ?? {}) as Record<string, unknown>,
    clientStatus: (r.client_status as string | null) ?? null,
    entityType: (r.entity_type as string | null) ?? null,
    hasCredentials: Boolean(r.has_credentials),
  }));

  rows.sort((a, b) => a.order - b.order);
  return { columns, rows };
}

/**
 * Employees for assignee pickers — reuses the portal's getEmployees()
 * (profiles with a non-null employee_role). Loaded ONCE per view; assignee
 * values in row_data are stored as UPPERCASE employee names.
 */
export async function listEmployees(): Promise<EmployeeOption[]> {
  const employees = await getEmployees();
  return employees
    .filter(e => e.name)
    .map(e => {
      // getEmployees selects * from profiles; avatar fields ride along even
      // though the (stale) generated EmployeeProfile type may not list them
      const raw = e as unknown as Record<string, unknown>;
      return {
        id: e.id,
        name: e.name as string,
        avatarUrl: (raw.avatar_url as string | null) ?? null,
        avatarColor: (raw.avatar_color as string | null) ?? null,
      };
    });
}

export interface ClientSearchResult {
  id: string;
  legal_name: string;
  status: string | null;
}

/** Search the clients table (V1's search_clients_v2 RPC, called as-is). */
export async function searchClients(
  term: string
): Promise<ClientSearchResult[]> {
  const { data, error } = await rpc('search_clients_v2', {
    search_term: term,
    include_inactive: true,
    limit_count: 20,
  });
  if (error) throw new Error(`Client search failed: ${error.message}`);
  return ((data ?? []) as Array<Record<string, unknown>>).map(c => ({
    id: String(c.id),
    legal_name: String(c.legal_name ?? ''),
    status: (c.status as string | null) ?? null,
  }));
}

/**
 * Create a client record via V1's upsert_client_v2 (client_uuid omitted =
 * create — the same call Clients V2's Add Client dialog makes). Status starts
 * active; every other field is filled in afterwards in the account sheet.
 * Returns the new client's UUID.
 */
export async function createClientForWorkflow(
  legalName: string,
  entityType: string | null
): Promise<string> {
  const { data, error } = await rpc('upsert_client_v2', {
    legal_name_input: legalName,
    entity_type_input: entityType ?? undefined,
    status_input: 'active',
    language_preference_input: 'en',
  });
  if (error) throw new Error(`Failed to create client: ${error.message}`);
  const id = Array.isArray(data) ? data[0] : data;
  if (typeof id !== 'string' || !id) {
    throw new Error('Failed to create client: no id returned');
  }
  return id;
}

/**
 * Add a row to a workflow month linked to a client, in ONE call: V1's
 * add_spreadsheet_row (SECURITY DEFINER, granted to authenticated) with
 * client_uuid set — the same shape SalesTax/Extensions/LLC/Bookkeeping V2
 * use. Payload is V1's minimal Add Row shape ({'Account Order': n}, no
 * default status); row_order is left to the RPC (appends).
 *
 * Previously this was add_tifpa_workflow_row_secure followed by a direct
 * `update({ client_id })`. That second step is rejected by the spreadsheet_rows
 * RLS policy (WITH CHECK updated_by = auth.uid(), never set) — every add
 * failed with "Account added but client link failed" and left an orphan
 * unlinked row behind. spreadsheet_rows.client_id is THE canonical clients
 * join, so the insert and the link must be atomic. Returns the new row's id.
 */
export async function addWorkflowRow(
  spreadsheetId: string,
  clientId: string,
  accountOrder: number
): Promise<string> {
  const { data, error } = await rpc('add_spreadsheet_row', {
    spreadsheet_uuid: spreadsheetId,
    row_data: { 'Account Order': accountOrder },
    client_uuid: clientId,
  });
  if (error) throw new Error(`Failed to add account: ${error.message}`);
  const rowId = typeof data === 'string' ? data : '';
  if (!rowId) throw new Error('Failed to add account: no row id returned');
  return rowId;
}

/**
 * Remove an account row from a workflow month — V1's own delete RPC
 * (delete_tifpa_workflow_row_secure: SECURITY DEFINER, tifpa_workflow%
 * sheets only). Deletes ONLY this month's spreadsheet row; the client record
 * and every other month's row are untouched. The RPC has no role check of
 * its own, so the caller gates the UI (Manager+).
 */
export async function deleteWorkflowRow(rowId: string): Promise<void> {
  const { error } = await rpc('delete_tifpa_workflow_row_secure', {
    row_uuid: rowId,
  });
  if (error) throw new Error(`Failed to remove account: ${error.message}`);
}

/**
 * Client-owned service/flag fields, by column display name → clients-table
 * column. The loader overlays these from `clients` onto every client-linked
 * row, so for linked rows they MUST be saved to `clients` — a row_data write
 * would be masked by the overlay on the next load. They can't go through
 * update_tifpa_workflow_row_secure either: its flag routing expects legacy
 * 'Yes' text (booleans silently save as false) and it doesn't route
 * QB Process / 1099 / S Corp Election at all. Saved via the V2-owned
 * update_client_services_for_v2 instead (sql/workflow-v2/005).
 */
export const CLIENT_SERVICE_COLUMNS: Record<string, string> = {
  'Sales Tax': 'sales_tax',
  Payroll: 'payroll',
  Bookkeeping: 'bookkeeping',
  '1099': 'service_1099',
  'S Corp Election': 's_corp_election',
  'QB Process': 'qb_process',
  // Tax File Ready tab (sql/workflow-v2/011): booleans, except tax_1099k —
  // a jsonb {option: boolean} object the RPC stores as-is
  W2: 'tax_w2',
  W3: 'tax_w3',
  W4: 'tax_w4',
  W9: 'tax_w9',
  '1099K': 'tax_1099k',
  '1099R': 'tax_1099r',
  K1: 'tax_k1',
  'ENDING INVENTORY': 'tax_ending_inventory',
  'RENTAL PROPERTY': 'tax_rental_property',
  'BROILER STATEMENT REPORT': 'tax_broiler_statement_report',
  '1095A': 'tax_1095a',
  'ID PIN': 'tax_id_pin',
  'STOCK STATEMENT': 'tax_stock_statement',
  CRYPTO: 'tax_crypto',
  // On Demand Billing tab (sql/workflow-v2/012): free text ('1099s' — named
  // to avoid the '1099' service flag above) + {date, username} jsonb stamps
  // the RPC stores as-is (a cleared stamp is normalized to NULL server-side)
  '1099s': 'odb_1099s',
  'CPA Letter': 'odb_cpa_letter',
  Census: 'odb_census',
  'Call IRS/State': 'odb_call_irs_state',
  Consulting: 'odb_consulting',
  'Insurance Audit': 'odb_insurance_audit',
  'LLC Renewal': 'odb_llc_renewal',
  'LLC Setup': 'odb_llc_setup',
  'WC Report': 'odb_wc_report',
  Other: 'odb_other',
};

/**
 * Client-owned assignee/frequency fields, by column display name →
 * clients-table column. Canonical in `clients` (Clients V2 / Sales Tax V2 /
 * Bank Data V2 read the same columns): the assignee columns hold profiles
 * UUIDs, payroll_frequency holds the clients value set
 * ('Semi-Monthly'/'Bi-Weekly'/...). The 009 loader overlays them as the
 * workflow's display shapes (UPPERCASE names / V1 frequency keys), falling
 * back to row_data when the clients value is NULL — which is where the
 * special non-employee options (NA / No Sales Tax / ...) live, since a UUID
 * column can't hold them. Saves therefore DUAL-WRITE: clients (canonical) +
 * row_data via the V1 RPC (V1 sheets, special options, unlinked rows).
 */
export const CLIENT_ASSIGNEE_COLUMNS: Record<string, string> = {
  'Account Manager': 'account_manager',
  'Sales Taxes Assigned To': 'sales_taxes_assigned_to',
  'Payroll Taxes Assigned To': 'payroll_assigned_to',
  'Payroll Frequency': 'payroll_frequency',
};

/** Workflow payroll-frequency key → clients.payroll_frequency value
 *  (unmapped values — legacy text, Quarterly/Annual — pass through as-is) */
const WORKFLOW_TO_CLIENTS_FREQUENCY: Record<string, string> = {
  SemiMonthly: 'Semi-Monthly',
  BiWeekly: 'Bi-Weekly',
};

/**
 * The clients-table value for one CLIENT_ASSIGNEE_COLUMNS save, or null when
 * the value has no clients representation ('' clears; special options and
 * unknown names live in row_data only, so clients gets NULL and the loader
 * falls back).
 */
export function clientsAssigneeValue(
  columnName: string,
  value: unknown,
  employees: EmployeeOption[]
): string | null {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return null;
  if (columnName === 'Payroll Frequency') {
    return WORKFLOW_TO_CLIENTS_FREQUENCY[raw] ?? raw;
  }
  const upper = raw.toUpperCase();
  return employees.find(e => e.name.toUpperCase() === upper)?.id ?? null;
}

/**
 * Save one clients assignee/frequency column — a direct clients UPDATE, the
 * same path updateClientStatus and Sales Tax V2's assigned-to editor use
 * (RLS governs access).
 */
export async function updateClientAssigneeField(
  clientId: string,
  clientsColumn: string,
  value: string | null
): Promise<void> {
  const { data, error } = await supabase
    .from('clients')
    .update({ [clientsColumn]: value, updated_at: new Date().toISOString() })
    .eq('id', clientId)
    .select('id');
  if (error) {
    throw new Error(`Failed to save: ${error.message}`);
  }
  // RLS turns a forbidden update into a silent 0-row no-op
  assertRowsUpdated(data);
}

/**
 * clients.bookkeepers value for a workflow 'Bookkeeper' save: V1's
 * comma-joined UPPERCASE-names string mapped to profile UUIDs. Unmatched
 * names (legacy text, special options) are skipped — they persist via the
 * row_data dual-write and display through the loader's fallback.
 */
export function clientsBookkeepersValue(
  value: unknown,
  employees: EmployeeOption[]
): string[] {
  if (typeof value !== 'string' || !value.trim()) return [];
  return value
    .split(',')
    .map(v => v.trim().toUpperCase())
    .filter(Boolean)
    .map(upper => employees.find(e => e.name.toUpperCase() === upper)?.id)
    .filter((id): id is string => Boolean(id));
}

/** Save clients.bookkeepers (jsonb array of profiles UUIDs — sql/workflow-v2/009) */
export async function updateClientBookkeepers(
  clientId: string,
  bookkeeperIds: string[]
): Promise<void> {
  const { data, error } = await supabase
    .from('clients')
    .update({
      bookkeepers: bookkeeperIds,
      updated_at: new Date().toISOString(),
    })
    .eq('id', clientId)
    .select('id');
  if (error) {
    const hint = /bookkeepers/i.test(error.message)
      ? ' (run sql/workflow-v2/009_client_assignee_overlay.sql in the SQL editor)'
      : '';
    throw new Error(`Failed to save: ${error.message}${hint}`);
  }
  assertRowsUpdated(data);
}

/**
 * Save one client service/flag field (CLIENT_SERVICE_COLUMNS) straight to the
 * clients table for a client-linked row. Real boolean for the service flags,
 * text for qb_process — no 'Yes'/'No' translation.
 */
export async function updateClientServiceField(
  clientId: string,
  clientsColumn: string,
  value: unknown
): Promise<void> {
  const { error } = await rpc('update_client_services_for_v2', {
    client_uuid: clientId,
    updates: { [clientsColumn]: value },
  });
  if (error) {
    const hint = /function|schema cache/i.test(error.message)
      ? ' (run sql/workflow-v2/005_update_client_services_for_v2.sql in the SQL editor)'
      : '';
    throw new Error(`Failed to save: ${error.message}${hint}`);
  }
}

/**
 * Save clients.status for a client-linked row — a direct clients UPDATE, the
 * same path Clients V2's saveDirect uses for its clients-only fields (no RPC
 * involved; RLS governs access). The workflow loader overlays client_status
 * onto rows, so callers must mirror the new value into local row state.
 */
export async function updateClientStatus(
  clientId: string,
  status: string
): Promise<void> {
  const { data, error } = await supabase
    .from('clients')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', clientId)
    .select('id');
  if (error) {
    throw new Error(`Failed to save client status: ${error.message}`);
  }
  assertRowsUpdated(data);
}

/**
 * Save the "ACH Payor" flag for a client-linked row. NOT part of
 * CLIENT_SERVICE_COLUMNS: it writes through set_ach_payor_flag_v2
 * (sql/ach-payors-v2/003) — the same RPC the ACH Payors page uses to add or
 * remove a payor — so the workflow toggle and /ach-payors can never disagree.
 * That RPC is Manager/Admin/SuperAdmin only; other roles get its access-denied
 * error inline in the cell.
 */
export async function updateAchPayorFlag(
  clientId: string,
  isPayor: boolean
): Promise<void> {
  const { error } = await rpc('set_ach_payor_flag_v2', {
    client_uuid: clientId,
    is_payor: isPayor,
  });
  if (error) {
    const hint = /function|schema cache/i.test(error.message)
      ? ' (run sql/ach-payors-v2/003_ach_payor_flag.sql in the SQL editor)'
      : '';
    throw new Error(`Failed to save: ${error.message}${hint}`);
  }
}

/** Value shape for date_with_user / month_with_user columns (V1-identical). */
export interface DateWithUserValue {
  date: string | null;
  username: string | null;
}

/**
 * Save ONE field of a workflow row through V1's own write path:
 * update_tifpa_workflow_row_secure with a single-column payload. The RPC
 * merges into row_data server-side, routes client identity fields to
 * `clients` (encrypted), and re-encrypts sensitive row_data fields — so
 * concurrent V1 users and backend writebacks stay consistent. Existing
 * function, called as-is (never modified), works for all month duplicates
 * (builtin_key 'tifpa_workflow%'). NOT for CLIENT_SERVICE_COLUMNS on
 * client-linked rows — use updateClientServiceField for those.
 *
 * Payload shapes (must stay V1-identical — see CellEditorOverlay.tsx):
 *   Account Status            → plain string ('' = no status)
 *   date/month_with_user set  → { date: 'YYYY-MM-DD', username: profiles.name }
 *   date/month_with_user clear→ { date: null, username: null }
 *   boolean                   → true | false
 *   employee_assignee         → UPPERCASE name, special option, or ''
 *   multi_employee_assignee   → 'NAME1, NAME2' comma-joined UPPERCASE
 */
export async function updateWorkflowField(
  rowId: string,
  columnName: string,
  value: unknown
): Promise<void> {
  const { error } = await rpc('update_tifpa_workflow_row_secure', {
    target_row_id: rowId,
    updated_data: { [columnName]: value },
  });
  if (error) {
    throw new Error(`Failed to save ${columnName}: ${error.message}`);
  }
}

/** Decrypted clients-table bank numbers (Manager+ only server-side). */
export interface ClientBankFields {
  routing_number: string | null;
  account_number: string | null;
}

function asBankFields(data: unknown): ClientBankFields {
  const obj = (data ?? {}) as Record<string, unknown>;
  return {
    routing_number: (obj.routing_number as string | null) ?? null,
    account_number: (obj.account_number as string | null) ?? null,
  };
}

const BANK_FIELDS_HINT =
  ' (run sql/workflow-v2/007_client_bank_fields_v2.sql in the SQL editor)';

/**
 * Routing/Account No for one client, decrypted server-side. These live
 * ENCRYPTED in the clients table (routing_no_encrypted/account_no_encrypted —
 * the same columns /ach-payors edits) and are never part of the workflow
 * loader payload, so the Info rows fetch them on demand. Manager+ only.
 */
export async function getClientBankFields(
  clientId: string
): Promise<ClientBankFields> {
  const { data, error } = await rpc('get_client_bank_fields_v2', {
    client_uuid: clientId,
  });
  if (error) {
    const hint = /function|schema cache/i.test(error.message)
      ? BANK_FIELDS_HINT
      : '';
    throw new Error(`Failed to load bank fields: ${error.message}${hint}`);
  }
  return asBankFields(data);
}

/**
 * Overwrite BOTH bank numbers for one client (encrypted server-side; the
 * caller fetches current values first and sends the full pair). Manager+
 * only; the RPC re-validates (9-digit routing, 1-17 digit account).
 */
export async function updateClientBankFields(
  clientId: string,
  routingNumber: string | null,
  accountNumber: string | null
): Promise<ClientBankFields> {
  const { data, error } = await rpc('update_client_bank_fields_v2', {
    client_uuid: clientId,
    routing_number_input: routingNumber,
    account_number_input: accountNumber,
  });
  if (error) {
    const hint = /function|schema cache/i.test(error.message)
      ? BANK_FIELDS_HINT
      : '';
    throw new Error(`Failed to save: ${error.message}${hint}`);
  }
  return asBankFields(data);
}

// Federal Tax ID access goes through V1's Client System V2 RPCs so every
// reveal/copy/edit is audit-logged to client_access_log server-side —
// same functions TaxIDRenderer uses in the V1 grid (existing, called as-is).

/** Full decrypted Tax ID for display (server logs the access) */
export async function getClientTaxIdFull(clientId: string): Promise<string> {
  const { data, error } = await rpc('get_client_tax_id_full_v2', {
    client_uuid: clientId,
  });
  if (error) throw new Error(error.message);
  return typeof data === 'string' ? data : '';
}

/** Full decrypted Tax ID for the clipboard (server logs the copy) */
export async function copyClientTaxId(clientId: string): Promise<string> {
  const { data, error } = await rpc('copy_client_tax_id_v2', {
    client_uuid: clientId,
  });
  if (error) throw new Error(error.message);
  return typeof data === 'string' ? data : '';
}

/** Save the Tax ID into the clients table (encrypts + logs server-side) */
export async function updateClientTaxId(
  clientId: string,
  taxId: string
): Promise<void> {
  const { error } = await rpc('update_client_tax_id_v2', {
    client_uuid: clientId,
    new_tax_id: taxId,
  });
  if (error) {
    throw new Error(`Failed to save Federal Tax ID: ${error.message}`);
  }
}

/**
 * Best-effort display string for a row_data value. Assignee/date values are
 * stored as objects ({ value, user, name, ... }) and multi-assignee columns
 * as arrays.
 */
export function displayValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(displayValue).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    for (const key of ['name', 'label', 'value', 'employee_name', 'date']) {
      if (typeof obj[key] === 'string' && obj[key]) return obj[key] as string;
    }
  }
  return '';
}
